import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  CurrencyYenIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { API_BASE } from '../lib/config';

// API base is provided by shared config (CRA/Next compatible)
const API = `${API_BASE}`;

const UpcomingWorkPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [upcomingWork, setUpcomingWork] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcomingWork();
  }, []);

  const fetchUpcomingWork = async () => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.get('/applications');
      // マッチング成立済みなど
      // タイミーでは 'accepted' (マッチング後) または 'confirmed'
      const acceptedApplications = response.data.filter(app =>
        (app.status === 'accepted' || app.status === 'confirmed') &&
        app.work_date &&
        new Date(app.work_date) >= new Date(new Date().setHours(0, 0, 0, 0)) // Today or future
      );

      // 勤務日順にソート
      acceptedApplications.sort((a, b) => new Date(a.work_date) - new Date(b.work_date));

      setUpcomingWork(acceptedApplications);
    } catch (error) {
      console.error('Failed to fetch upcoming work:', error);
      toast.error('今後の予定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      await authApi.post(`/jobs/${jobId}/confirm-attendance`);
      toast.success('前日確認が完了しました！');
      fetchUpcomingWork(); // Refresh
    } catch (error) {
      console.error(error);
      toast.error('確認に失敗しました');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return '今日';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return '明日';
    } else {
      return date.toLocaleDateString('ja-JP', {
        month: 'short',
        day: 'numeric',
        weekday: 'short'
      });
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.slice(0, 5); // HH:MM format
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">予定を読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-6 w-6 mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold text-gray-900">今後の予定</h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-6">
        {upcomingWork.length === 0 ? (
          <div className="text-center py-12">
            <CalendarDaysIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">予定されたお仕事はありません</h3>
            <p className="text-gray-600 mb-6">
              マッチング成立したお仕事がある場合、ここに表示されます
            </p>
            <Link
              to="/jobs"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              お仕事を探す
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingWork.map((work) => (
              <div key={work.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                    マッチング成立
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDate(work.work_date)}
                  </span>
                </div>

                {/* Job Title and Company */}
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {work.job_title || '求人タイトル'}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-2">
                    <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">{work.business_name}</span>
                  </div>
                  {work.employer_name && (
                    <div className="flex items-center text-gray-600">
                      <UserIcon className="h-4 w-4 mr-2" />
                      <span className="text-sm">{work.employer_name}</span>
                    </div>
                  )}
                </div>

                {/* Work Details */}
                <div className="space-y-3 mb-4">
                  {/* Date and Time */}
                  <div className="flex items-center text-gray-700">
                    <CalendarDaysIcon className="h-5 w-5 mr-3 text-blue-500" />
                    <div>
                      <div className="font-medium">
                        {new Date(work.work_date).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          weekday: 'long'
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Preferred Work Hours */}
                  {work.preferred_work_start_time && work.preferred_work_end_time && (
                    <div className="flex items-center text-gray-700">
                      <ClockIcon className="h-5 w-5 mr-3 text-green-500" />
                      <div>
                        <div className="font-medium">
                          {formatTime(work.preferred_work_start_time)} - {formatTime(work.preferred_work_end_time)}
                        </div>
                        <div className="text-sm text-gray-500">希望勤務時間</div>
                      </div>
                    </div>
                  )}

                  {/* Location (if available) */}
                  {work.work_location && (
                    <div className="flex items-center text-gray-700">
                      <MapPinIcon className="h-5 w-5 mr-3 text-red-500" />
                      <div>
                        <div className="font-medium">{work.work_location}</div>
                      </div>
                    </div>
                  )}

                  {/* Hourly Rate (if available) */}
                  {work.hourly_rate && (
                    <div className="flex items-center text-gray-700">
                      <CurrencyYenIcon className="h-5 w-5 mr-3 text-yellow-500" />
                      <div>
                        <div className="font-medium text-green-600">
                          時給 {work.hourly_rate.toLocaleString()}円
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Message if available */}
                {work.message && (
                  <div className="bg-blue-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-900">
                      <span className="font-medium">申込メッセージ:</span> {work.message}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  {!work.is_attendance_confirmed ? (
                    <button
                      onClick={() => handleConfirmAttendance(work.job_id)}
                      className="w-full bg-red-600 text-white py-3 rounded-lg font-bold shadow-sm hover:bg-red-700 transition-colors animate-pulse"
                    >
                      前日確認を行う (必須)
                    </button>
                  ) : (
                    <div className="w-full bg-green-100 text-green-800 py-2 rounded-lg text-center font-bold border border-green-200 cursor-default">
                      <CheckCircleIconSolid className="inline w-5 h-5 mr-1 mb-1" />
                      前日確認済み
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/messages/${work.id}`)}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      メッセージ
                    </button>
                    {work.job_id && (
                      <Link
                        to={`/jobs/${work.job_id}`}
                        className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium text-center hover:bg-gray-200 transition-colors"
                      >
                        詳細確認
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tips */}
        {upcomingWork.length > 0 && (
          <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <span className="text-blue-600 text-sm"></span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">お仕事の準備</h4>
                <p className="text-sm text-blue-700">
                  勤務前日には、事業者とメッセージで最終確認を行いましょう。
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingWorkPage;